/*
 * Brenory UI: shared, dependency-free custom elements and helpers.
 * Elements: brn-slider, brn-accordion, brn-marquee, brn-countdown,
 *           brn-ba-slider, brn-video, brn-sticky-cta
 * Helpers on window.Brn: reducedMotion(), parseDate(), formatMoney(), formatPrice(),
 *           scrollTo(), headerOffset(), openDetails()
 */
(function () {
  'use strict';

  var Brn = (window.Brn = window.Brn || {});
  var docEl = document.documentElement;
  var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reducedMotion() {
    return !!(reduceMQ && reduceMQ.matches);
  }

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function define(name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  }

  function safeDecode(str) {
    try { return decodeURIComponent(str); } catch (e) { return str; }
  }

  function emit(el, name, detail) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  Brn.reducedMotion = reducedMotion;

  /** Parses an ISO 8601 string ("2026-10-06T23:59:59-04:00"). Returns ms or null. */
  Brn.parseDate = function (raw) {
    if (!raw) return null;
    var s = String(raw).trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2} \d/.test(s)) s = s.replace(' ', 'T');
    var t = Date.parse(s);
    if (isNaN(t) && /[+-]\d{4}$/.test(s)) t = Date.parse(s.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
    return isNaN(t) ? null : t;
  };

  /** Formats cents with the shop money format (window.Brn.moneyFormat, set in layout/brn.liquid). */
  Brn.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || Brn.moneyFormat || '{{amount_with_comma_separator}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(n, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var parts = (n / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var m = format.match(re);
    var value;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(re, value);
  };

  /** Like formatMoney, but 0 prints "GRATIS" (never "0,00"). */
  Brn.formatPrice = function (cents, format) {
    return (parseInt(cents, 10) || 0) === 0 ? 'GRATIS' : Brn.formatMoney(cents, format);
  };

  /** Height of the sticky header currently covering the top of the viewport. */
  Brn.headerOffset = function () {
    var v = parseFloat(getComputedStyle(docEl).getPropertyValue('--brn-header-offset'));
    return isNaN(v) ? 0 : v;
  };

  function headerHeightFor(targetTop) {
    var h = parseFloat(getComputedStyle(docEl).getPropertyValue('--brn-header-h')) || 0;
    var mode = docEl.getAttribute('data-brn-sticky') || 'none';
    if (mode === 'always') return h;
    if (mode === 'scroll-up') return targetTop < 0 ? h : 0; // header re-appears when scrolling up
    return 0;
  }

  /** Smooth-scrolls to an element (or selector), leaving room for the sticky header. */
  Brn.scrollTo = function (target, opts) {
    var el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    opts = opts || {};
    var rect = el.getBoundingClientRect();
    var extra = opts.offset != null ? opts.offset : 12;
    var top = window.pageYOffset + rect.top - headerHeightFor(rect.top) - extra;
    window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion() ? 'auto' : 'smooth' });
    if (opts.focus) {
      if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(el.tagName)) {
        el.setAttribute('tabindex', '-1');
      }
      try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
  };

  /** Opens a <details> (animated when it belongs to a brn-accordion). */
  Brn.openDetails = function (details) {
    if (!details || details.tagName !== 'DETAILS' || details.open) return;
    var acc = details.closest('brn-accordion');
    if (acc && typeof acc.open === 'function') acc.open(details);
    else details.open = true;
  };

  /* Same-page hash links: open a target <details> (or the one around it) and scroll with header offset. */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest ? e.target.closest('a[href*="#"]') : null;
    if (!a || a.hasAttribute('data-no-scroll') || a.target === '_blank') return;
    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.hash.length < 2) return;
    var target = document.getElementById(safeDecode(url.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    var details = target.tagName === 'DETAILS' ? target : target.closest('details');
    if (details) Brn.openDetails(details);
    Brn.scrollTo(target, { focus: true });
  });

  /* ------------------------------------------------------------------ */
  /* <brn-slider>                                                         */
  /* ------------------------------------------------------------------ */
  /*
   * Markup:
   * <brn-slider [data-loop] [data-autoplay="5000"] [data-label="Reviews"]>
   *   <div class="brn-slider__track" data-slider-track>
   *     <div class="brn-slider__slide">…</div> …
   *   </div>
   *   <button class="brn-slider__btn brn-slider__btn--prev" data-slider-prev aria-label="Previous">…</button>
   *   <button class="brn-slider__btn brn-slider__btn--next" data-slider-next aria-label="Next">…</button>
   *   <div class="brn-slider__dots" data-slider-dots></div>      (dots are generated)
   *   <span data-slider-counter></span> / <span data-slider-total></span>  ("01" / "05")
   *   <button data-slider-thumb="0">…</button>                     (thumbs, slide index, 0-based)
   * </brn-slider>
   * API: el.goTo(i), el.next(), el.prev(), el.index, el.count
   * Event: "brn:slider:change" {index, count, slide} (bubbles)
   */
  class BrnSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        /* Re-attached (theme editor moved a section): re-observe what disconnect tore down. */
        if (this._ro) this._ro.observe(this.track);
        if (this._io) this._io.observe(this);
        if (this._onVis) document.addEventListener('visibilitychange', this._onVis);
        this.refresh();
        this._pauseAutoplay(false);
        return;
      }
      this.track = this.querySelector('[data-slider-track]') || this.querySelector('.brn-slider__track');
      if (!this.track) return;
      this._ready = true;
      this.index = 0;
      this.count = 0;
      this.loop = this.hasAttribute('data-loop');
      this._onScroll = this._onScroll.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onKey = this._onKey.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKey);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._setupDrag();

      if (!this.track.hasAttribute('role')) {
        this.track.setAttribute('role', 'region');
        this.track.setAttribute('aria-roledescription', 'carousel');
        if (this.dataset.label) this.track.setAttribute('aria-label', this.dataset.label);
      }

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this.refresh());
        this._ro.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.refresh());
      }
      this.refresh();
      this._setupAutoplay();
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
      clearInterval(this._timer);
      this._timer = null;
    }

    get slides() {
      return Array.prototype.filter.call(this.track.children, function (el) {
        return el.offsetParent !== null || el.getClientRects().length > 0;
      });
    }

    /* Recomputes snap positions (call after adding/removing slides). */
    refresh() {
      var slides = this.slides;
      var track = this.track;
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      var base = slides.length ? slides[0].offsetLeft : 0;
      var positions = [];
      var slideToPage = [];
      slides.forEach(function (s) {
        var p = clamp(s.offsetLeft - base, 0, max);
        var last = positions[positions.length - 1];
        if (!positions.length || Math.abs(p - last) > 2) positions.push(p);
        slideToPage.push(positions.length - 1);
      });
      if (!positions.length) positions = [0];
      this.positions = positions;
      this.slideToPage = slideToPage;
      this._slidesCache = slides;
      if (positions.length !== this.count) {
        this.count = positions.length;
        this._buildDots();
      }
      this.classList.toggle('is-static', this.count <= 1);
      this._syncIndex(true);
    }

    _buildDots() {
      var wrap = this.querySelector('[data-slider-dots]');
      if (!wrap) return;
      wrap.innerHTML = '';
      if (this.count <= 1) return;
      for (var i = 0; i < this.count; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'brn-slider__dot';
        b.setAttribute('data-slider-dot', i);
        b.setAttribute('aria-label', 'Przejdź do slajdu ' + (i + 1));
        wrap.appendChild(b);
      }
    }

    _onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._syncIndex(false);
      });
    }

    _syncIndex(force) {
      var x = this.track.scrollLeft;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var pos = this.positions || [0];
      var idx = 0;
      if (x >= max - 2 && max > 0) {
        idx = pos.length - 1;
      } else {
        var best = Infinity;
        for (var i = 0; i < pos.length; i++) {
          var d = Math.abs(pos[i] - x);
          if (d < best) { best = d; idx = i; }
        }
      }
      if (idx !== this.index || force) {
        this.index = idx;
        this._update();
      }
    }

    _update() {
      var i = this.index;
      var last = this.count - 1;
      var loop = this.loop;
      this.querySelectorAll('[data-slider-prev]').forEach(function (b) { b.disabled = !loop && i <= 0; });
      this.querySelectorAll('[data-slider-next]').forEach(function (b) { b.disabled = !loop && i >= last; });
      this.querySelectorAll('[data-slider-dot]').forEach(function (d) {
        d.setAttribute('aria-current', String(Number(d.getAttribute('data-slider-dot')) === i));
      });
      var counter = this.querySelector('[data-slider-counter]');
      if (counter) counter.textContent = pad2(i + 1);
      var total = this.querySelector('[data-slider-total]');
      if (total) total.textContent = pad2(this.count);
      var map = this.slideToPage || [];
      this.querySelectorAll('[data-slider-thumb]').forEach(function (t) {
        var on = map[Number(t.getAttribute('data-slider-thumb'))] === i;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      var slides = this._slidesCache || [];
      slides.forEach(function (s, n) { s.classList.toggle('is-active', map[n] === i); });
      this._scrollThumbIntoView();
      emit(this, 'brn:slider:change', { index: i, count: this.count, slide: slides[map.indexOf(i)] || null });
    }

    _scrollThumbIntoView() {
      var t = this.querySelector('[data-slider-thumb].is-active');
      if (!t) return;
      var strip = t.parentElement;
      while (strip && strip !== this && !(strip.scrollWidth > strip.clientWidth + 1 || strip.scrollHeight > strip.clientHeight + 1)) {
        strip = strip.parentElement;
      }
      if (!strip || strip === this) return;
      var sr = strip.getBoundingClientRect();
      var tr = t.getBoundingClientRect();
      var behavior = reducedMotion() ? 'instant' : 'smooth';
      if (strip.scrollWidth > strip.clientWidth + 1) {
        if (tr.left < sr.left || tr.right > sr.right) {
          strip.scrollTo({ left: strip.scrollLeft + (tr.left - sr.left) - (sr.width - tr.width) / 2, behavior: behavior });
        }
      } else if (tr.top < sr.top || tr.bottom > sr.bottom) {
        strip.scrollTo({ top: strip.scrollTop + (tr.top - sr.top) - (sr.height - tr.height) / 2, behavior: behavior });
      }
    }

    goTo(i, instant) {
      if (!this.count) return;
      if (this.loop) i = (i + this.count) % this.count;
      else i = clamp(i, 0, this.count - 1);
      this.track.scrollTo({ left: this.positions[i], behavior: instant || reducedMotion() ? 'instant' : 'smooth' });
      if (i !== this.index) {
        this.index = i;
        this._update();
      }
    }

    goToSlide(slideIndex, instant) {
      var p = (this.slideToPage || [])[slideIndex];
      if (p != null) this.goTo(p, instant);
    }

    next() { this.goTo(this.index + 1); }
    prev() { this.goTo(this.index - 1); }

    _onClick(e) {
      var t = e.target;
      var btn = t.closest('[data-slider-prev],[data-slider-next],[data-slider-dot],[data-slider-thumb]');
      if (!btn || btn.closest('brn-slider') !== this) return;
      e.preventDefault();
      this._pauseAutoplay(true);
      if (btn.hasAttribute('data-slider-prev')) this.prev();
      else if (btn.hasAttribute('data-slider-next')) this.next();
      else if (btn.hasAttribute('data-slider-dot')) this.goTo(Number(btn.getAttribute('data-slider-dot')));
      else this.goToSlide(Number(btn.getAttribute('data-slider-thumb')));
    }

    _onKey(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable) return;
      if (!this.track.contains(e.target) && !e.target.closest('[data-slider-dots],[data-slider-thumb]')) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') this.prev();
      else this.next();
    }

    /* Mouse drag-to-scroll (touch uses native scrolling). */
    _setupDrag() {
      var track = this.track;
      var state = null;
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      // Swallow the click that follows a mouse drag (capture phase, before links/buttons react).
      track.addEventListener('click', (e) => {
        if (!this._suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
        this._suppressClick = false;
      }, true);
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (track.scrollWidth <= track.clientWidth + 1) return;
        state = { id: e.pointerId, x: e.clientX, left: track.scrollLeft, moved: false };
      });
      track.addEventListener('pointermove', (e) => {
        if (!state || e.pointerId !== state.id) return;
        var dx = e.clientX - state.x;
        if (!state.moved && Math.abs(dx) > 6) {
          state.moved = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
          this._pauseAutoplay(true);
        }
        if (state.moved) track.scrollLeft = state.left - dx;
      });
      var end = (e) => {
        if (!state || (e && e.pointerId !== state.id)) return;
        var moved = state.moved;
        var dx = e ? e.clientX - state.x : 0;
        state = null;
        if (!moved) return;
        track.classList.remove('is-dragging');
        this._suppressClick = true;
        setTimeout(() => { this._suppressClick = false; }, 0);
        this._syncIndex(false);
        var i = this.index;
        var x = track.scrollLeft;
        var cur = this.positions[i];
        if (dx < -30 && cur < x + 2) i += 1;
        else if (dx > 30 && cur > x - 2) i -= 1;
        this.goTo(clamp(i, 0, this.count - 1));
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      track.addEventListener('lostpointercapture', function () {
        if (state && state.moved) track.classList.remove('is-dragging');
      });
    }

    _setupAutoplay() {
      var ms = parseInt(this.getAttribute('data-autoplay'), 10);
      if (!ms || ms < 1500 || reducedMotion()) return;
      this._autoplayMs = ms;
      this._visible = true;
      var resume = () => this._pauseAutoplay(false);
      var pause = () => this._pauseAutoplay(true, true);
      this.addEventListener('pointerenter', pause);
      this.addEventListener('pointerleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.track.addEventListener('touchstart', () => this._pauseAutoplay(true), { passive: true });
      this._onVis = () => (document.hidden ? pause() : resume());
      document.addEventListener('visibilitychange', this._onVis);
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          this._visible = entries[0].isIntersecting;
          this._visible ? resume() : pause();
        });
        this._io.observe(this);
      }
      resume();
    }

    /* stop=true for user interaction (stops autoplay for good), temporary=true for hover/focus. */
    _pauseAutoplay(stop, temporary) {
      clearInterval(this._timer);
      this._timer = null;
      if (stop && !temporary) this._autoplayStopped = true;
      if (!stop && this._autoplayMs && !this._autoplayStopped && this._visible !== false && this.count > 1) {
        this._timer = setInterval(() => {
          if (!this.loop && this.index >= this.count - 1) this.goTo(0);
          else this.next();
        }, this._autoplayMs);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-accordion>                                                      */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-accordion [data-single]>
   *   <details class="brn-acc__item" id="…" [open]>
   *     <summary class="brn-acc__summary">Title <span class="brn-acc__icon">{% render 'brn-icon', name: 'plus' %}</span></summary>
   *     <div class="brn-acc__content">…</div>
   *   </details>
   * </brn-accordion>
   * API: el.open(details), el.close(details), el.toggle(details)
   * Event: "brn:accordion:toggle" {details, open}
   * Same-page links to a details id (or anything inside one) open it automatically.
   */
  class BrnAccordion extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.single = this.hasAttribute('data-single');
      this.addEventListener('click', (e) => {
        var summary = e.target.closest('summary');
        if (!summary) return;
        var details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS' || details.closest('brn-accordion') !== this) return;
        if (e.target.closest('a[href]') && e.target.closest('a[href]') !== summary) return;
        e.preventDefault();
        this.toggle(details);
      });
      /* Keep aria-expanded in sync with every open-state change, including
         browser-initiated ones (find-in-page auto-expand). 'toggle' does not bubble. */
      this.addEventListener('toggle', function (e) {
        var d = e.target;
        if (!d || d.tagName !== 'DETAILS') return;
        var s = d.querySelector(':scope > summary');
        if (s) s.setAttribute('aria-expanded', String(d.open && !d._brnClosing));
      }, true);
      if (location.hash.length > 1) {
        var t = document.getElementById(safeDecode(location.hash.slice(1)));
        if (t && this.contains(t)) {
          var d = t.tagName === 'DETAILS' ? t : t.closest('details');
          if (d) d.open = true;
        }
      }
      this.querySelectorAll('details').forEach(function (d) {
        var s = d.querySelector(':scope > summary');
        if (s) s.setAttribute('aria-expanded', String(d.open));
      });
    }

    toggle(details) {
      if (details.open && !details._brnClosing) this.close(details);
      else this.open(details);
    }

    open(details) {
      if (this.single) {
        this.querySelectorAll('details[open]').forEach((d) => {
          if (d !== details && d.closest('brn-accordion') === this) this.close(d);
        });
      }
      var summary = details.querySelector(':scope > summary');
      var startH = details.offsetHeight;
      this._cancel(details);
      details._brnClosing = false;
      details.open = true;
      if (summary) summary.setAttribute('aria-expanded', 'true');
      emit(this, 'brn:accordion:toggle', { details: details, open: true });
      if (reducedMotion() || !details.animate) return;
      var endH = details.offsetHeight;
      this._animate(details, startH, endH, function () {});
    }

    close(details) {
      var summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      emit(this, 'brn:accordion:toggle', { details: details, open: false });
      if (reducedMotion() || !details.animate || !summary) {
        details.open = false;
        return;
      }
      var cs = getComputedStyle(details);
      var startH = details.offsetHeight;
      var endH = summary.offsetHeight +
        parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) +
        parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      this._cancel(details);
      details._brnClosing = true;
      this._animate(details, startH, endH, function () {
        details.open = false;
        details._brnClosing = false;
      });
    }

    _cancel(details) {
      if (details._brnAnim) {
        details._brnAnim.onfinish = null;
        details._brnAnim.cancel();
        details._brnAnim = null;
      }
      details.style.overflow = '';
    }

    _animate(details, from, to, done) {
      details.style.overflow = 'hidden';
      var anim = details.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: 280, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
      );
      details._brnAnim = anim;
      anim.onfinish = function () {
        details._brnAnim = null;
        details.style.overflow = '';
        done();
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-marquee>                                                        */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-marquee [data-speed="40"] [data-direction="right"] [data-pause-on-hover]>
   *   <div class="brn-marquee__track">
   *     <div class="brn-marquee__group"> item • item • item </div>
   *   </div>
   * </brn-marquee>
   * The group is cloned (aria-hidden) until it fills twice the width; speed is px/second.
   */
  class BrnMarquee extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        if (this._ro) this._ro.observe(this);
        return;
      }
      this.track = this.querySelector('.brn-marquee__track');
      this.group = this.track && this.track.querySelector('.brn-marquee__group');
      if (!this.group) return;
      this._ready = true;
      this.speed = parseFloat(this.getAttribute('data-speed')) || 40;
      if (reducedMotion()) return;
      /* WCAG 2.2.2: a tap/click (outside links) pauses or resumes the ticker; focus also pauses it (CSS). */
      this.addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('a[href], button')) return;
        this.classList.toggle('is-paused');
      });
      this._w = 0;
      this.build();
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => {
          var w = Math.round(this.getBoundingClientRect().width);
          if (w !== this._w) this.build();
        });
        this._ro.observe(this);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.build());
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
    }

    build() {
      var track = this.track;
      track.querySelectorAll('[data-marquee-clone]').forEach(function (n) { n.remove(); });
      var gw = this.group.getBoundingClientRect().width;
      var cw = this.getBoundingClientRect().width;
      this._w = Math.round(cw);
      if (!gw || !cw) return;
      var copies = Math.max(1, Math.ceil(cw / gw));
      for (var i = 1; i < copies * 2; i++) {
        var c = this.group.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-marquee-clone', '');
        c.querySelectorAll('a, button, input, [tabindex]').forEach(function (n) { n.setAttribute('tabindex', '-1'); });
        c.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
        track.appendChild(c);
      }
      this.style.setProperty('--brn-marquee-duration', ((copies * gw) / this.speed).toFixed(2) + 's');
      this.classList.add('is-ready');
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-countdown>                                                      */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-countdown data-end="2026-10-06T23:59:59-04:00" [data-hide-closest=".my-bar"]>
   *   Either unit slots: <span data-cd="days"></span> <span data-cd="hours"></span>
   *                      <span data-cd="minutes"></span> <span data-cd="seconds"></span>
   *   or an output slot <span data-cd-output></span> (gets "hh:mm:ss"),
   *   or nothing (the element's own text becomes "hh:mm:ss").
   * </brn-countdown>
   * data-end defaults to window.Brn.saleEnd (settings.brn_sale_end).
   * Blank/invalid/past end → the element (and data-hide-closest ancestor) gets [hidden].
   * Without a days slot, hours include whole days (e.g. 47:05:09).
   * Event: "brn:countdown:end" when it reaches zero.
   */
  class BrnCountdown extends HTMLElement {
    connectedCallback() {
      if (this._timer) return;
      var raw = this.hasAttribute('data-end') ? this.getAttribute('data-end') : Brn.saleEnd;
      this.end = Brn.parseDate(raw);
      if (!this.hasAttribute('role')) this.setAttribute('role', 'timer');
      if (!this.end || this.end <= Date.now()) {
        this._expire(false);
        return;
      }
      this._unhide();
      this._tick();
      this._timer = setInterval(() => this._tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this._timer);
      this._timer = null;
    }

    _target() {
      var sel = this.getAttribute('data-hide-closest');
      return sel ? this.closest(sel) : null;
    }

    _unhide() {
      this.hidden = false;
      var c = this._target();
      if (c && c.hasAttribute('data-cd-wait')) c.hidden = false;
    }

    _tick() {
      var ms = this.end - Date.now();
      if (ms <= 0) {
        this._expire(true);
        return;
      }
      var total = Math.floor(ms / 1000);
      var d = Math.floor(total / 86400);
      var h = Math.floor((total % 86400) / 3600);
      var m = Math.floor((total % 3600) / 60);
      var s = total % 60;
      var daysEl = this.querySelector('[data-cd="days"]');
      var hours = daysEl ? h : d * 24 + h;
      var slots = {
        days: String(d),
        hours: pad2(hours),
        minutes: pad2(m),
        seconds: pad2(s)
      };
      var any = false;
      this.querySelectorAll('[data-cd]').forEach(function (el) {
        var key = el.getAttribute('data-cd');
        if (key in slots) {
          any = true;
          if (el.textContent !== slots[key]) el.textContent = slots[key];
        }
      });
      if (daysEl && this.hasAttribute('data-hide-zero-days')) {
        var unit = daysEl.closest('[data-cd-unit]') || daysEl;
        unit.hidden = d === 0;
      }
      var text = pad2(hours) + ':' + pad2(m) + ':' + pad2(s);
      var out = this.querySelector('[data-cd-output]');
      if (out) out.textContent = text;
      else if (!any) this.textContent = text;
    }

    _expire(fire) {
      clearInterval(this._timer);
      this._timer = null;
      this.hidden = true;
      var c = this._target();
      if (c) c.hidden = true;
      if (fire) emit(this, 'brn:countdown:end', {});
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-ba-slider>                                                      */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-ba-slider [data-start="50"] style="--brn-ratio: 1/1">
   *   <div class="brn-ba__after"><img …></div>          (full image, right side)
   *   <div class="brn-ba__before"><img …></div>         (clipped image, left side)
   *   <span class="brn-ba__label brn-ba__label--before">Before</span>
   *   <span class="brn-ba__label brn-ba__label--after">After</span>
   * </brn-ba-slider>
   * A visually-hidden range input (keyboard) and the handle are created if missing.
   * Event: "brn:ba:change" {value}
   */
  class BrnBaSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.range = this.querySelector('.brn-ba__range');
      if (!this.range) {
        this.range = document.createElement('input');
        this.range.type = 'range';
        this.range.className = 'brn-ba__range brn-sr-only';
        this.range.min = '0';
        this.range.max = '100';
        this.range.step = '1';
        this.range.setAttribute('aria-label', this.getAttribute('data-label') || 'Porównanie przed i po');
        this.appendChild(this.range);
      }
      if (!this.querySelector('.brn-ba__handle')) {
        var handle = document.createElement('span');
        handle.className = 'brn-ba__handle';
        handle.setAttribute('aria-hidden', 'true');
        handle.innerHTML =
          '<span class="brn-ba__knob"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 7-5 5 5 5M15 7l5 5-5 5"/></svg></span>';
        this.appendChild(handle);
      }
      this.set(parseFloat(this.getAttribute('data-start')) || 50);
      this.range.addEventListener('input', () => this.set(this.range.value));

      var drag = null;
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        drag = { id: e.pointerId, x: e.clientX, y: e.clientY, active: e.pointerType === 'mouse' };
        if (drag.active) this._start(e);
      });
      this.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        if (!drag.active) {
          var dx = Math.abs(e.clientX - drag.x);
          var dy = Math.abs(e.clientY - drag.y);
          if (dx < 4 || dx < dy) return;
          drag.active = true;
          this._start(e);
        }
        this._move(e);
      });
      var end = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        if (!drag.active && e.type === 'pointerup' && e.pointerType !== 'mouse') this._move(e); // tap
        drag = null;
        this.classList.remove('is-dragging');
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
    }

    _start(e) {
      this.classList.add('is-dragging');
      try { this.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      this._move(e);
    }

    _move(e) {
      var r = this.getBoundingClientRect();
      if (!r.width) return;
      this.set(((e.clientX - r.left) / r.width) * 100);
    }

    set(v) {
      v = clamp(Number(v) || 0, 0, 100);
      this.value = v;
      this.style.setProperty('--brn-ba-pos', v + '%');
      if (this.range && Number(this.range.value) !== Math.round(v)) this.range.value = String(Math.round(v));
      emit(this, 'brn:ba:change', { value: v });
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-video>                                                          */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-video [data-preview] [data-muted] style="--brn-ratio: 9/16">
   *   <img class="brn-video__poster" src="…" alt="…" loading="lazy">     (optional)
   *   <template><video src="…" playsinline preload="metadata"></video></template>  (lazy)
   *     – or a <video> directly (use preload="none" + poster)
   *   <button type="button" class="brn-video__play" aria-label="Play video">{% render 'brn-icon', name: 'play' %}</button>
   * </brn-video>
   * Click plays with sound + native controls and pauses every other brn-video.
   * data-preview: muted inline loop while on screen (skipped for reduced motion).
   * API: el.play(), el.pause(). Events: "brn:video:play", "brn:video:pause".
   */
  class BrnVideo extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        if (this._io) this._io.observe(this);
        return;
      }
      this._ready = true;
      this.addEventListener('click', (e) => {
        if (this.classList.contains('is-playing') && e.target.closest('video')) return; // native controls
        e.preventDefault();
        this.play();
      });
      if (this.hasAttribute('data-preview') && !reducedMotion() && 'IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          var v = this._video(true);
          if (!v || this.classList.contains('is-playing')) return;
          if (entries[0].isIntersecting) {
            v.muted = true;
            v.loop = true;
            v.playsInline = true;
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
            this.classList.add('is-previewing');
          } else {
            v.pause();
          }
        }, { threshold: 0.4 });
        this._io.observe(this);
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }

    _video(create) {
      var v = this.querySelector('video');
      if (!v && create) {
        var tpl = this.querySelector('template');
        if (tpl) {
          this.appendChild(tpl.content.cloneNode(true));
          v = this.querySelector('video');
        }
      }
      if (v && !v._brnBound) {
        v._brnBound = true;
        v.setAttribute('playsinline', '');
        v.addEventListener('ended', () => this._stopped());
        v.addEventListener('pause', () => {
          if (this._external) this._stopped();
        });
      }
      return v;
    }

    play() {
      var v = this._video(true);
      if (!v) return;
      document.querySelectorAll('brn-video.is-playing').forEach((el) => {
        if (el !== this && typeof el.pause === 'function') el.pause();
      });
      this._external = false;
      this.classList.remove('is-previewing');
      if (this._io) {
        this._io.disconnect();
        this._io = null;
        v.loop = false;
        try { v.currentTime = 0; } catch (err) { /* noop */ }
      }
      v.muted = this.hasAttribute('data-muted');
      v.controls = true;
      this.classList.add('is-playing');
      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          v.muted = true;
          v.play().catch(function () {});
        });
      }
      emit(this, 'brn:video:play', {});
    }

    pause() {
      var v = this.querySelector('video');
      this._external = true;
      if (v && !v.paused) v.pause();
      else this._stopped();
    }

    _stopped() {
      var v = this.querySelector('video');
      if (v) v.controls = false;
      this.classList.remove('is-playing');
      this._external = false;
      emit(this, 'brn:video:pause', {});
    }
  }

  /* ------------------------------------------------------------------ */
  /* <brn-sticky-cta>                                                     */
  /* ------------------------------------------------------------------ */
  /*
   * <brn-sticky-cta [data-watch="[data-brn-atc]"] [data-target="[data-brn-bundles]"] [data-hide-when="#BrnFinalCta"]>
   *   <div class="brn-sticky-cta__inner">
   *     <button type="button" class="brn-btn brn-btn--block" data-sticky-cta-btn>Get yours now</button>
   *     <p class="brn-sticky-cta__sub">Claim your offer now</p>
   *   </div>
   * </brn-sticky-cta>
   * Shows once the watched element (main ATC) has scrolled above the viewport;
   * hides while any data-hide-when element is on screen. Clicking a button inside
   * (or a same-page "#…" link) scrolls to data-target.
   * Sets html.brn-sticky-cta-on + --brn-sticky-cta-h while visible.
   * Event: "brn:sticky-cta" {visible}
   */
  class BrnStickyCta extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        /* Re-attached (theme editor moved/reloaded a section): observe again. */
        this._teardown();
        this._observe();
        return;
      }
      this._ready = true;
      this.visible = false;
      this.passed = false;
      this.blockers = new Set();
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;

      this.addEventListener('click', (e) => {
        var b = e.target.closest('[data-sticky-cta-btn], button, a');
        if (!b) return;
        var href = b.tagName === 'A' ? b.getAttribute('href') || '' : '';
        if (href && href.charAt(0) !== '#') return;
        e.preventDefault();
        e.stopPropagation();
        this.go();
      });

      /* In the theme editor a re-rendered section replaces the watched ATC / blockers. */
      this._onSectionLoad = () => {
        if (!this.isConnected) return;
        this._teardown();
        this._observe();
      };
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      document.addEventListener('shopify:section:unload', this._onSectionLoad);

      var start = () => this._observe();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
      else start();
    }

    disconnectedCallback() {
      this._teardown();
      this.visible = false;
      this.passed = false;
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      docEl.classList.remove('brn-sticky-cta-on');
    }

    _teardown() {
      if (this._io) this._io.disconnect();
      if (this._io2) this._io2.disconnect();
      this._io = this._io2 = null;
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);
        window.removeEventListener('pageshow', this._onScroll);
        this._onScroll = null;
      }
      if (this.blockers) this.blockers.clear();
    }

    _observe() {
      this.watchEl = document.querySelector(this.getAttribute('data-watch') || '[data-brn-atc]');
      /*
       * Geometry-based "passed" state, rAF-throttled. An IntersectionObserver alone
       * misses jumps that move the ATC from below the viewport to above it (anchor
       * links, End/Home, scroll restoration, scrollbar drags) without intersecting.
       */
      var tick = false;
      this._onScroll = () => {
        if (tick) return;
        tick = true;
        requestAnimationFrame(() => {
          tick = false;
          if (this.watchEl && this.watchEl.isConnected) {
            this.passed = this.watchEl.getBoundingClientRect().bottom < 0;
          } else {
            this.passed = window.pageYOffset > window.innerHeight * 0.9;
          }
          this._update();
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      window.addEventListener('pageshow', this._onScroll);
      this._onScroll();

      var hideSel = this.getAttribute('data-hide-when');
      if (hideSel && 'IntersectionObserver' in window) {
        this._io2 = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) this.blockers.add(en.target);
            else this.blockers.delete(en.target);
          });
          this._update();
        });
        document.querySelectorAll(hideSel).forEach((el) => this._io2.observe(el));
      }
    }

    _update() {
      var show = this.passed && this.blockers.size === 0;
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.inert = !show;
      if (show) docEl.style.setProperty('--brn-sticky-cta-h', this.offsetHeight + 'px');
      docEl.classList.toggle('brn-sticky-cta-on', show);
      emit(this, 'brn:sticky-cta', { visible: show });
    }

    go() {
      var target = document.querySelector(this.getAttribute('data-target') || '[data-brn-bundles]') || this.watchEl;
      if (target) Brn.scrollTo(target, { offset: 12, focus: true });
    }
  }

  define('brn-slider', BrnSlider);
  define('brn-accordion', BrnAccordion);
  define('brn-marquee', BrnMarquee);
  define('brn-countdown', BrnCountdown);
  define('brn-ba-slider', BrnBaSlider);
  define('brn-video', BrnVideo);
  define('brn-sticky-cta', BrnStickyCta);

  Brn.ui = { version: '1.0.0' };
})();
